// Client/src/core/HttpClient.ts
// Zentrale Fetch-Abstraktion für das Frontend

import { isAbortError } from '../utils/errorMessage';

export interface RequestOptions<TBody = unknown> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  headers?: Record<string, string>;
  body?: TBody;
  signal?: AbortSignal;
  credentials?: RequestCredentials;
  /**
   * Falls true, wird ein Authorization-Header mit dem aktuellen Token angehängt,
   * sofern eines verfügbar ist. Standard: true.
   */
  auth?: boolean;
}

export interface HttpClientConfig {
  baseUrl?: string;
  getAccessToken?: () => string | null | undefined;
}

export class HttpError<TData = unknown> extends Error {
  readonly status: number;
  readonly data: TData | null;
  readonly causeError: unknown;

  constructor(status: number, message: string, data: TData | null, cause?: unknown) {
    super(message);
    this.status = status;
    this.data = data;
    this.causeError = cause;
    this.name = 'HttpError';
  }
}

export interface HttpResponse<TData = unknown> {
  data: TData;
  status: number;
  headers: Headers;
}

export class HttpClient {
  private readonly baseUrl?: string;
  private readonly getAccessToken?: () => string | null | undefined;

  constructor(config: HttpClientConfig = {}) {
    this.baseUrl = config.baseUrl;
    this.getAccessToken = config.getAccessToken;
  }

  async request<TResponse = unknown, TBody = unknown>(
    url: string,
    options: RequestOptions<TBody> = {}
  ): Promise<TResponse> {
    const result = await this.performFetch<TResponse, TBody>(url, options);
    return result.payload;
  }

  async requestWithMeta<TResponse = unknown, TBody = unknown>(
    url: string,
    options: RequestOptions<TBody> = {}
  ): Promise<HttpResponse<TResponse>> {
    const result = await this.performFetch<TResponse, TBody>(url, options);
    return {
      data: result.payload,
      status: result.status,
      headers: result.headers,
    };
  }

  get<TResponse = unknown>(url: string, options?: Omit<RequestOptions, 'method' | 'body'>) {
    return this.request<TResponse>(url, { ...options, method: 'GET' });
  }

  post<TResponse = unknown, TBody = unknown>(
    url: string,
    body?: TBody,
    options?: Omit<RequestOptions<TBody>, 'method' | 'body'>
  ) {
    return this.request<TResponse, TBody>(url, { ...options, method: 'POST', body });
  }

  put<TResponse = unknown, TBody = unknown>(
    url: string,
    body?: TBody,
    options?: Omit<RequestOptions<TBody>, 'method' | 'body'>
  ) {
    return this.request<TResponse, TBody>(url, { ...options, method: 'PUT', body });
  }

  patch<TResponse = unknown, TBody = unknown>(
    url: string,
    body?: TBody,
    options?: Omit<RequestOptions<TBody>, 'method' | 'body'>
  ) {
    return this.request<TResponse, TBody>(url, { ...options, method: 'PATCH', body });
  }

  delete<TResponse = unknown>(url: string, options?: Omit<RequestOptions, 'method' | 'body'>) {
    return this.request<TResponse>(url, { ...options, method: 'DELETE' });
  }

  /**
   * Laedt eine binaere Ressource (z. B. ein PDF) als Blob. Nutzt dieselbe Auth-
   * und URL-Logik wie die JSON-Requests, umgeht aber das JSON/Text-Parsing.
   */
  async getBlob(url: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<Blob> {
    const { headers = {}, signal, credentials = 'include', auth = true } = options;
    const requestHeaders: Record<string, string> = { ...headers };
    if (auth !== false && this.getAccessToken) {
      const token = this.getAccessToken();
      if (token) {
        requestHeaders.Authorization = requestHeaders.Authorization ?? `Bearer ${token}`;
      }
    }

    try {
      const response = await fetch(this.resolveUrl(url), {
        method: 'GET',
        headers: requestHeaders,
        signal,
        credentials,
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const message =
          (payload && typeof payload === 'object' && 'message' in payload && typeof payload.message === 'string'
            ? payload.message
            : response.statusText) || 'Unbekannter Fehler';
        throw new HttpError(response.status, message, payload);
      }
      return await response.blob();
    } catch (error) {
      if (error instanceof HttpError) throw error;
      if (isAbortError(error)) throw error;
      throw new HttpError(0, error instanceof Error ? error.message : 'Netzwerkfehler', null, error);
    }
  }

  private async performFetch<TResponse = unknown, TBody = unknown>(
    url: string,
    options: RequestOptions<TBody>
  ): Promise<{ payload: TResponse; status: number; headers: Headers }> {
    const {
      method = 'GET',
      headers = {},
      body,
      signal,
      credentials = 'include',
      auth = true,
    } = options;

    const resolvedUrl = this.resolveUrl(url);
    const requestHeaders: Record<string, string> = {
      Accept: 'application/json',
      ...headers,
    };

    let requestBody: BodyInit | undefined;

    if (body instanceof FormData || body instanceof URLSearchParams) {
      requestBody = body as BodyInit;
    } else if (body !== undefined && body !== null) {
      requestHeaders['Content-Type'] = requestHeaders['Content-Type'] ?? 'application/json';
      if (requestHeaders['Content-Type'] === 'application/json') {
        requestBody = JSON.stringify(body);
      } else {
        requestBody = body as BodyInit;
      }
    }

    if (auth !== false && this.getAccessToken) {
      const token = this.getAccessToken();
      if (token) {
        requestHeaders.Authorization = requestHeaders.Authorization ?? `Bearer ${token}`;
      }
    }

    try {
      const response = await fetch(resolvedUrl, {
        method,
        headers: requestHeaders,
        body: requestBody,
        signal,
        credentials,
      });

      const isJson = response.headers.get('content-type')?.includes('application/json');
      const payload = isJson
        ? await response.json().catch(() => null)
        : await response.text().catch(() => null);

      if (!response.ok) {
        const message =
          (payload && typeof payload === 'object' && 'message' in payload && typeof payload.message === 'string'
            ? payload.message
            : response.statusText) || 'Unbekannter Fehler';
        throw new HttpError(response.status, message, payload);
      }

      return {
        payload: payload as TResponse,
        status: response.status,
        headers: response.headers,
      };
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      if (isAbortError(error)) {
        throw error;
      }
      // Netzwerk- oder Abort-Fehler
      throw new HttpError(
        0,
        error instanceof Error ? error.message : 'Netzwerkfehler',
        null,
        error
      );
    }
  }

  private resolveUrl(url: string) {
    if (!this.baseUrl || url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `${this.baseUrl.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}`;
  }
}
